import React, { Component, Fragment } from 'react';
import { Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import StarRatings from 'react-star-ratings';
import ProfileCompletion from '../../common/ProfileCompletion';
import ProfileSuggestions from '../../common/ProfileSuggestions';
import InviteColleagues from '../../common/InviteColleagues';
import EditRoles from '../EditRoles';
import EditUser from '../EditUser';
import { getRequest, postRequest, deleteRequest } from '../../../requestUtils';
import { analyticsTrack } from '../../../analyticsUtils';
import { toRealNumber } from '../../../numberUtils';
import './UserSection.scss';

const nonFullViewName = (firstName, lastName, showHireAgain) => {
  if (showHireAgain) {
    return [firstName, lastName].join(' ');
  } if (firstName && lastName) {
    return `${firstName} ${lastName[0]}.`;
  } if (firstName) {
    return `${firstName}`;
  }
  return 'Name Omitted';
};

const name = (firstName, lastName, settingsUrl) => {
  if (firstName || lastName) {
    return <h1 className="fs-hide">{`${[firstName, lastName].join(' ')}`}</h1>;
  }
  return (
    <h1>
      <a
        tabIndex={0}
        role="button"
        href={settingsUrl}
      >
        Add Name
      </a>
    </h1>
  );
};

const renderField = (attribute, icon, answer) => (
  <span className={`${attribute} body-text-2 me-2`}>
    <i className={`fa ${icon} me-2`} />
    {answer}
  </span>
);

function hourlyRateText({ low, high }) {
  if (low && high) {
    return (
      <div className="mb-2">
        <span className="demibold">
          $
          {toRealNumber(low)}
          {' '}
          -
          {toRealNumber(high)}
        </span>
        {' '}
        /hour
      </div>
    );
  }
  if (low || high) {
    return (
      <div className="mb-2">
        <span className="demibold">
          $
          {low ? toRealNumber(low) : toRealNumber(high)}
        </span>
        {' '}
        /hour
      </div>
    );
  }
  return null;
}

const projectProposals = (receivedProposals, fromProject) => (
  receivedProposals.find((p) => p === fromProject)
);

class UserSection extends Component {
  constructor(props) {
    super(props);
    const { receivedProposals, fromProject } = this.props;
    this.state = {
      answers: null,
      favoriteId: props.favoriteId,
      proposalRequested: projectProposals(receivedProposals, fromProject) || false,
    };
    this.toggleSave = this.toggleSave.bind(this);
    this.getAnswers = this.getAnswers.bind(this);
    this.requestProposal = this.requestProposal.bind(this);
  }

  componentDidMount() {
    this.getAnswers()
      .then((response) => {
        this.setState({ answers: response.data });
      });
  }

  getAnswers() {
    const {
      questionCategoryId, questionableId, questionableType, questionnaireName,
    } = this.props;
    return getRequest(
      '/question_answers/answers',
      {
        questionnaire_name: questionnaireName,
        question_category_id: questionCategoryId,
        questionable_id: questionableId,
        questionable_type: questionableType,
      },
    );
  }

  toggleSave() {
    const { id, csrfToken, searchEnabled } = this.props;
    const { favoriteId } = this.state;

    if (searchEnabled) {
      if (favoriteId) {
        deleteRequest(
          `/user_favorites/${favoriteId}`,
          csrfToken,
        ).then(() => { this.setState({ favoriteId: null }); });
      } else {
        postRequest(
          '/user_favorites',
          {
            user_favorite: {
              favorited_id: id,
              favorited_type: 'ConsultantProfile',
            },
          },
          csrfToken,
        ).then((response) => {
          this.setState({ favoriteId: response.data });
          analyticsTrack('Clicked Save on Profile View', { location: 'Consultant Profile Page' });
          window.location.href = '/users/favorites';
        });
      }
    }
  }

  requestProposal() {
    const {
      csrfToken, resourceId, consultantInitials, fromProject,
    } = this.props;
    const toastMessage = `You requested a proposal from ${consultantInitials} Clora will be in touch shortly with next steps.`;
    postRequest(
      `/projects/${fromProject}/project_invitations`,
      { consultant_resource_id: resourceId, request_proposal: true },
      csrfToken,
    ).then(() => {
      analyticsTrack('Clicked on Request Proposal', {
        location: 'Consultant Profile redirect from Project View',
        resource_id: resourceId,
      });
    });
    toast.success(
      <span>
        {toastMessage}
      </span>,
    );
    this.setState({ proposalRequested: true });
  }

  renderActions() {
    const {
      searchEnabled,
      showHireAgain,
      uninvitedColleagues,
      invitedColleagues,
      hourlyRate,
      query,
      resourceId,
      csrfToken,
      inviteUrl,
      inviterEmailDomain,
      fromProject,
      firstName,
      lastName,
    } = this.props;

    const { favoriteId, proposalRequested } = this.state;
    const showCTA = showHireAgain || fromProject || searchEnabled;

    if (showCTA) {
      return (
        <Col xs={12} md={3} className="text-center g-column">
          {hourlyRate && hourlyRateText(hourlyRate)}
          <Fragment>
            {showHireAgain
              && (
              <a
                href={`/users/${resourceId}/hire_again`}
                target="_blank"
                className="btn btn-primary get-connected profile-get-connected"
                onClick={() => {
                  analyticsTrack('Consultant hired again', { location: 'Consultant Profile Page', consultant: `${firstName} ${lastName}` });
                }}
                type="button"
                id="hire-again"
                rel="noreferrer"
              >
                Hire Again
              </a>
              )}
            {(!showHireAgain && fromProject)
              && (
              <a
                role="button"
                type="button"
                className={`btn btn-primary ${proposalRequested ? 'disabled' : ''} get-connected profile-get-connected`}
                onClick={this.requestProposal}
                tabIndex={0}
                id="project-request"
              >
                Request Proposal
              </a>
              )}
            {(!showHireAgain && !fromProject && searchEnabled)
              && (
              <a
                href={`/users/${resourceId}/request_proposal?query_id=${query}`}
                role="button"
                type="button"
                target="_blank"
                className={`btn btn-primary ${proposalRequested ? 'disabled' : ''} get-connected profile-get-connected`}
                onClick={() => {
                  analyticsTrack('Click Request Proposal on Profile', { location: 'Profile' });
                }}
                tabIndex={0}
                id="search-request"
                rel="noreferrer"
              >
                Request Proposal
              </a>
              )}
            <div className="g-row--always g-row--centered">
              <InviteColleagues
                disabled={false}
                location="User Show View"
                invitedColleagues={invitedColleagues}
                uninvitedColleagues={uninvitedColleagues}
                csrfToken={csrfToken}
                resource="Profile"
                resourceId={resourceId}
                inviteUrl={inviteUrl}
                handleFeedbacks={this.handleChange}
                styleType="icon"
                styleClass="fa fa-share-alt"
                cta="Share"
                header="Share with colleagues"
                subtext="Send this profile to other team members
                    and make a decision about this candidate."
                ownerMessagePlaceholder="How does this candidate fit your needs?..."
                limitInvitesWithinCompany
                inviterEmailDomain={inviterEmailDomain}
                confirmationScreenAfterSend
                preventCloseModalAfterSubmit
              />
              <button
                className="btn btn-secondary-no-border"
                onClick={this.toggleSave}
                type="button"
              >
                <i className={`${favoriteId ? 'fas' : 'far'} fa-heart`} />
                <span className="ps-3">Save</span>
              </button>
            </div>
          </Fragment>
        </Col>
      );
    }

    return null;
  }

  render() {
    const {
      firstName,
      lastName,
      settingsUrl,
      editMode,
      fullView,
      csrfToken,
      questionableId,
      questionableType,
      questionnaireName,
      userSectionQuestionnaireQuestions,
      rolesQuestionnaireQuestions,
      updateCompletionPercentage,
      togglePublic,
      isPublic,
      percentage,
      availableNow,
      ratingAvg,
      reviewsLength,
      rolesAvailable,
      showHireAgain,
      suggestedTags,
      updateSuggestedTags,
      showSuggestionModal,
      userId,
    } = this.props;
    const { answers } = this.state;
    const role = answers && answers.find((a) => a.question_key === 'Roles');

    const suggestedTagCount = Object.keys(suggestedTags).reduce((sum, key) => sum + suggestedTags[key].length, 0);

    if (answers && answers.length > 0) {
      if (fullView) {
        return (
          <div className="user-section">
            <div className="profile-section">
              <Row>
                <Col xs={12} sm={12} md={8}>
                  <div className="g-row--always mb-2 ">
                    {name(firstName, lastName, settingsUrl)}
                    <div className="rating-avg">
                      <div className="g-row--always">
                        <StarRatings
                          rating={ratingAvg || 0}
                          starRatedColor="#EDA318"
                          starHoverColor="#EDA318"
                          starDimension="20px"
                          starSpacing="1px"
                          numberOfStars={5}
                          name="rating_avg"
                        />
                        <div className="body-text-2 medium-weight avg">
                          {(ratingAvg && toRealNumber(ratingAvg, 1)) || 0}
                        </div>
                        <div className="body-text-2">
                          (
                          {reviewsLength}
                          {' '}
                          review
                          {reviewsLength === 1 ? '' : 's'}
                          )
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    {role && rolesAvailable
                      && (
                      <EditRoles
                        csrfToken={csrfToken}
                        editMode={editMode}
                        answers={answers}
                        questionableId={questionableId}
                        questionableType={questionableType}
                        questionnaireQuestions={rolesQuestionnaireQuestions}
                        questionnaireName={questionnaireName}
                        getAnswers={this.getAnswers}
                        updateCompletionPercentage={updateCompletionPercentage}
                        suggestedTags={suggestedTags}
                        updateSuggestedTags={updateSuggestedTags}
                        showSuggestionModal={showSuggestionModal}
                        fullView={fullView}
                      />
                      )}
                  </div>
                  <div className="mb-2">
                    {answers && answers.find((a) => a.question_key === 'Location')
                      && (
                      <EditUser
                        csrfToken={csrfToken}
                        editMode={editMode}
                        answers={answers}
                        questionableId={questionableId}
                        questionableType={questionableType}
                        questionnaireQuestions={userSectionQuestionnaireQuestions}
                        questionnaireName={questionnaireName}
                        getAnswers={this.getAnswers}
                        updateCompletionPercentage={updateCompletionPercentage}
                        suggestedTags={suggestedTags}
                        updateSuggestedTags={updateSuggestedTags}
                        showSuggestionModal={showSuggestionModal}
                        userId={userId}
                      />
                      )}
                  </div>
                  {suggestedTagCount > 0
                    ? (
                      <ProfileSuggestions
                        tags={suggestedTagCount > 0}
                        small
                      />
                    )
                    : (
                      <ProfileCompletion
                        header="Strengthen your profile!"
                        subtext="Rank higher, get more projects"
                        percentage={percentage}
                        small
                      />
                    )}
                </Col>
                <Col xs={12} sm={6} md={4} className="d-none d-md-block">
                  <a role="button" tabIndex={0} id={`profile-page-make-${isPublic ? 'private' : 'public'}-button`} className="g-row--always g-row--between demibold" onClick={() => togglePublic()}>
                    <div>
                      {isPublic ? <i className="fa fa-lock-open" /> : <i className="fa fa-lock" />}
                      {isPublic ? 'Public Profile' : 'Private Profile'}
                    </div>
                    <div className="switch">
                      <input
                        type="checkbox"
                        checked={isPublic}
                        readOnly
                      />
                      <span className="slider round" />
                    </div>
                  </a>
                  {isPublic
                    ? (
                      <div className="public-explanation body-text-3">
                        Employers on Clora can view your public profile. Your resume and other
                        personally identifiable information won&apos;t be visible.
                      </div>
                    )
                    : (
                      <div className="private-explanation body-text-3">
                        <i className="fa fa-exclamation-triangle" />
                        Employers on Clora cannot view your profile.
                      </div>
                    )}
                </Col>
              </Row>
            </div>
            <div className="d-block d-md-none d-lg-none">
              <a role="button" tabIndex={0} id={`profile-page-make-${isPublic ? 'public' : 'private'}-button`} className="g-row--always g-row--between demibold" onClick={() => togglePublic()}>
                <div>
                  {isPublic ? <i className="fa fa-lock-open" /> : <i className="fa fa-lock" />}
                  {isPublic ? 'Public Profile' : 'Private View'}
                </div>
                <div className="switch">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    readOnly
                  />
                  <span className="slider round" />
                </div>
              </a>
            </div>
            <Row>
              <Col xs={12} sm={6} md={3} className="d-none d-md-block" />
            </Row>
          </div>
        );
      }
      const location = answers.find((a) => a.question_key === 'Location');
      const seniorities = answers.find((a) => a.question_key === 'Role Level');
      const yearsOfExperience = answers.find((a) => a.question_key === 'Years Of Experience');

      return (
        <div className="profile-section user-section">
          <Row>
            <Col xs={12} md={9}>
              <div className="g-row--always mb-2">
                <h1 className="fs-hide">{nonFullViewName(firstName, lastName, showHireAgain)}</h1>
                {ratingAvg
                  && (
                  <div className="rating-avg">
                    <div className="g-row--always align-items-center">
                      <StarRatings
                        rating={ratingAvg}
                        starRatedColor="#EDA318"
                        starHoverColor="#EDA318"
                        starDimension="20px"
                        starSpacing="1px"
                        numberOfStars={5}
                        name="rating_avg"
                      />
                      <div className="body-text-2 medium-weight avg">
                        {toRealNumber(ratingAvg, 1)}
                      </div>
                      <div className="body-text-2">
                        (
                        {reviewsLength}
                        )
                      </div>
                    </div>
                  </div>
                  )}
                {availableNow && <span className="available-now body-text-3">Available Now</span>}
              </div>
              <div className="level-yrs-location">
                <div className="mb-2">
                  {role && rolesAvailable
                  && (
                  <EditRoles
                    csrfToken={csrfToken}
                    editMode={editMode}
                    answers={answers}
                    questionableId={questionableId}
                    questionableType={questionableType}
                    questionnaireQuestions={rolesQuestionnaireQuestions}
                    questionnaireName={questionnaireName}
                    getAnswers={this.getAnswers}
                    updateCompletionPercentage={updateCompletionPercentage}
                    suggestedTags={suggestedTags}
                    updateSuggestedTags={updateSuggestedTags}
                    showSuggestionModal={showSuggestionModal}
                    fullView={fullView}
                  />
                  )}
                </div>
                {yearsOfExperience && yearsOfExperience.answer && renderField(
                  'years-of-experience',
                  'fa-id-card-alt',
                  `${yearsOfExperience.answer} year${yearsOfExperience.answer === 0 || yearsOfExperience.answer > 1 ? 's' : ''} of experience`,
                )}
                {seniorities && seniorities.answer && seniorities.answer.length > 0 && renderField('seniorities', 'fa-certificate', seniorities.answer.map((a) => a.label).join(', '))}
                {location && location.answer && renderField(
                  'location',
                  'fa-map-marker-alt',
                  location.answer,
                )}
              </div>
            </Col>
            { this.renderActions() }
          </Row>
        </div>
      );
    }
    return (
      <div className="profile-section user-section">
        <h1 className="mb-2 fs-hide">{nonFullViewName(firstName, lastName, showHireAgain)}</h1>
        <div className="body-text-2 medium-weight">Loading...</div>
      </div>
    );
  }
}

UserSection.propTypes = {
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  settingsUrl: PropTypes.string.isRequired,
  inviteUrl: PropTypes.string.isRequired,
  editMode: PropTypes.bool,
  fullView: PropTypes.bool.isRequired,
  csrfToken: PropTypes.string.isRequired,
  questionCategoryId: PropTypes.number.isRequired,
  questionableId: PropTypes.number.isRequired,
  questionableType: PropTypes.string.isRequired,
  questionnaireName: PropTypes.string.isRequired,
  userSectionQuestionnaireQuestions: PropTypes.arrayOf(PropTypes.number).isRequired,
  rolesQuestionnaireQuestions: PropTypes.arrayOf(PropTypes.number).isRequired,
  updateCompletionPercentage: PropTypes.func.isRequired,
  rolesAvailable: PropTypes.bool.isRequired,
  resourceId: PropTypes.string.isRequired,
  togglePublic: PropTypes.func.isRequired,
  isPublic: PropTypes.bool.isRequired,
  percentage: PropTypes.number.isRequired,
  availableNow: PropTypes.bool.isRequired,
  hourlyRate: PropTypes.shape({}).isRequired,
  ratingAvg: PropTypes.number,
  reviewsLength: PropTypes.number.isRequired,
  searchEnabled: PropTypes.bool.isRequired,
  query: PropTypes.number,
  uninvitedColleagues: PropTypes.arrayOf(PropTypes.shape({})),
  invitedColleagues: PropTypes.arrayOf(PropTypes.shape({})),
  userId: PropTypes.number.isRequired,
  id: PropTypes.number.isRequired,
  favoriteId: PropTypes.number,
  inviterEmailDomain: PropTypes.string,
  showHireAgain: PropTypes.bool,
  suggestedTags: PropTypes.shape({}),
  updateSuggestedTags: PropTypes.func,
  showSuggestionModal: PropTypes.string,
  fromProject: PropTypes.string,
  consultantInitials: PropTypes.string,
  receivedProposals: PropTypes.arrayOf(PropTypes.string),
};

hourlyRateText.propTypes = {
  low: PropTypes.string.isRequired,
  high: PropTypes.string.isRequired,
};

UserSection.defaultProps = {
  firstName: null,
  lastName: null,
  editMode: false,
  ratingAvg: null,
  favoriteId: null,
  query: null,
  inviterEmailDomain: '',
  showHireAgain: false,
  suggestedTags: {},
  updateSuggestedTags: () => {},
  showSuggestionModal: '',
  uninvitedColleagues: [],
  fromProject: null,
  consultantInitials: null,
  receivedProposals: [],
};

export default UserSection;
